import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { useUserContext } from './context/UserContext';
import { AppShell } from './components/layout';
import {
  ComplaintsList,
  ComplaintDetail,
  SubmitComplaint,
  StoresPage,
  SettingsPage,
  AnalyticsDashboard,
  SelectBrandPage,
  VendorAdminPage,
} from './pages';
import { AdminVendorManagement } from './components/AdminVendorManagement';
import { useStoresQuery } from './hooks/useStoresQuery';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ErrorBoundary } from './components/ui';
import { isAdmin } from './lib/roles';

// Re-export from routes.ts for backwards compatibility
export { ROUTES, getPageKeyFromPath } from './routes';

// Protected route wrapper that requires authentication
function ProtectedLayout() {
  const { user, isLoading, brandId } = useUserContext();

  // Only internal users have store access. Also, stores are brand-scoped in the backend.
  const shouldLoadStores = !!user && user.userType !== 'vendor' && !!brandId;
  const { data: storesData } = useStoresQuery(shouldLoadStores);
  const stores = storesData?.data || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (!user) {
    // This shouldn't happen as AuthRouter handles auth state
    return <Navigate to="/" replace />;
  }

  if (!brandId) {
    return <SelectBrandPage />;
  }

  return (
    <AppShell stores={stores}>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  );
}

// Admin route guard - requires any admin role (global or country)
function AdminGuard() {
  const { user } = useUserContext();

  if (!isAdmin(user?.roles)) {
    return <Navigate to="/complaints" replace />;
  }

  return <AdminVendorManagement />;
}

// Submit complaint guard - only internal users can submit
function SubmitComplaintGuard() {
  const { user } = useUserContext();
  const isVendor = user?.userType === 'vendor';

  if (isVendor) {
    return <Navigate to="/complaints" replace />;
  }

  return <SubmitComplaint />;
}

// Vendor admin guard - requires vendor user with admin role
function VendorAdminGuard() {
  const { user } = useUserContext();
  const isVendorAdmin = user?.userType === 'vendor' && user?.vendorRole === 'admin';

  if (!isVendorAdmin) {
    return <Navigate to="/complaints" replace />;
  }

  return <VendorAdminPage />;
}

// Create the router configuration
export const router = createBrowserRouter([
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/complaints" replace />,
      },
      {
        // Redirect old dashboard URLs to complaints for backward compatibility
        path: 'dashboard',
        element: <Navigate to="/complaints" replace />,
      },
      {
        path: 'complaints',
        children: [
          {
            index: true,
            element: <ComplaintsList />,
          },
          {
            path: 'new',
            element: <SubmitComplaintGuard />,
          },
          {
            path: ':id',
            element: <ComplaintDetail />,
          },
        ],
      },
      {
        path: 'analytics',
        element: <AnalyticsDashboard />,
      },
      {
        path: 'stores',
        element: <StoresPage />,
      },
      {
        path: 'admin',
        element: <AdminGuard />,
      },
      {
        path: 'team',
        element: <VendorAdminGuard />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
    ],
  },
]);
